import apiClient from './apiClient';

// ============================================================
// Types -- matching backend Go models for J201 (樣品申請單)
// ============================================================

export interface FJ110 {
  fj110_01: string;          // 申請單號 (PK, auto-generated)
  fj110_11: string | null;   // 申請日期 (ISO string)
  fj110_12: string;          // 客戶代號
  fj110_13: string | null;   // 需要日期 (ISO string)
  fj110_14: string;          // 幣別代號
  fj110_15: number;          // 匯率
  fj110_16: boolean;         // 收費旗標
  fj110_17: boolean;         // 含稅旗標
  fj110_171: number;         // 發票種類 (1~5)
  fj110_18: number;          // 原幣未稅總額 (display)
  fj110_181: number;         // 原幣含稅總額 (display)
  fj110_19: number;          // 稅額 (display)
  fj110_20: number;          // 預留整數欄
  fj110_21: string;          // 業務代號
  fj110_22: string;          // 部門代號 (auto-fill)
  fj110_23: number;          // 預留整數欄2
  fj110_24: string;          // 交運地點
  fj110_25: string;          // 客戶單號 (memo)
  fj110_26: boolean;         // 預留布林欄
  fj110_30: string;          // 備註代號
  fj110_31: string;          // 備註說明 (memo)
  fj110_32: number;          // 總項數 (display)
  fj110_33: boolean;         // 扣庫存旗標
  fj110_34: string;          // 參照欄位
  fj110_49: boolean;         // 預留布林欄2
  fj110_80: string;          // 核准狀態 N=草稿/已沖銷 D=送審中 P=已核准（自創碼 S/R/V 已廢除）
  fj110_90: number;          // 版本號
  fj110_91: string;          // 異動人員
  fj110_92: string | null;   // 異動日期 (ISO string)
  fj110_93: string;          // 建立人員
  details?: FJ111[];
}

export interface FJ111 {
  fj111_01: string;          // 申請單號 FK
  fj111_02: number;          // 項次
  fj111_10: string;          // 產品代號1
  fj111_11: string;          // 產品代號2
  fj111_12: string;          // 色號
  fj111_121: string;         // 中文色名 (auto-fill)
  fj111_122: string;         // 英文色名 (auto-fill)
  fj111_12a: string;         // CBNO
  fj111_13: number;          // 申請數量
  fj111_14: string;          // 單位
  fj111_15: number;          // 未稅售價
  fj111_151: number;         // 含稅售價
  fj111_16: number;          // 未稅金額 (computed)
  fj111_161: number;         // 含稅金額 (computed)
  fj111_17: string;          // 產品說明
  fj111_90: number;          // 版本號
}

// ============================================================
// Request / Response types
// ============================================================

export interface J201ListResponse {
  data: FJ110[];
  total: number;
  page: number;
  size: number;
}

export interface J201CreateRequest {
  master: Partial<FJ110>;
  details: Partial<FJ111>[];
}

export interface ValidationError {
  field: string;
  rule: string;
  message: string;
}

// ============================================================
// Approval types
// ============================================================

export interface ApprovalResponse {
  request_no: string;
  action: string;       // SUBMIT | APPROVE | REJECT
  old_status: string;
  new_status: string;
  message: string;
}

export interface ApprovalHistory {
  id: number;
  request_no: string;
  action: string;
  actor_id: string;
  actor_name: string;
  comment: string;
  old_status: string;
  new_status: string;
  created_at: string;
}

// ============================================================
// API methods
// ============================================================

export const j201Api = {
  async list(page = 1, size = 20): Promise<J201ListResponse> {
    const res = await apiClient.get('/api/j201', { params: { page, size } });
    return res.data;
  },

  async get(id: string): Promise<FJ110> {
    const res = await apiClient.get(`/api/j201/${encodeURIComponent(id)}`);
    return res.data;
  },

  async create(data: J201CreateRequest): Promise<FJ110> {
    const res = await apiClient.post('/api/j201', data);
    return res.data;
  },

  async update(id: string, data: J201CreateRequest): Promise<FJ110> {
    const res = await apiClient.put(`/api/j201/${encodeURIComponent(id)}`, data);
    return res.data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/api/j201/${encodeURIComponent(id)}`);
  },

  // ============================================================
  // Approval workflow
  // ============================================================

  async submitForApproval(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/j201/${encodeURIComponent(id)}/submit`, { comment: comment || '' });
    return res.data;
  },

  async approve(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/j201/${encodeURIComponent(id)}/approve`, { comment: comment || '' });
    return res.data;
  },

  async reject(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/j201/${encodeURIComponent(id)}/reject`, { comment: comment || '' });
    return res.data;
  },

  async writeoff(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/j201/${encodeURIComponent(id)}/writeoff`, { comment: comment || '' });
    return res.data;
  },

  async getApprovalHistory(id: string): Promise<ApprovalHistory[]> {
    const res = await apiClient.get(`/api/j201/${encodeURIComponent(id)}/approval-history`);
    return res.data.data || [];
  },

  // Lookup: get display name for a reference table code
  // type: employee | customer | department | currency | remarkTemplate
  async lookupName(type: string, code: string): Promise<string> {
    if (!code || !code.trim()) return '';
    try {
      // Use SC module's lookup endpoint (shared across modules)
      const res = await apiClient.get(`/api/sc/lookup/${encodeURIComponent(type)}/${encodeURIComponent(code.trim())}`);
      return res.data.name || '';
    } catch {
      return '';
    }
  },
};
